/*------------------------------------------------------------------------------
   About      : Typed catalog of proxypulse BFF feature endpoint strings (RBAC v2)

   proxypulse (the Flask BFF, sibling repo) gates its own routes with its own
   feature-based RBAC: `@require_feature()` matches Flask's `request.endpoint`
   (`<blueprint>.<view function name>`) against a `DAFeature.endpoint` row.
   Strapi-direct modules are NOT cataloged (legacy role if/else) — these
   strings are proxypulse-only. Only actions actually called from quill-fe
   (via `rc.bffClient`/`BffApiClient.ts` or the CX editor client) are listed;
   every entry here has a live FE caller.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Leaves } from './catalogTypes'

export const BFF_FEATURES = {

  CX : {
    LIST_TEMPLATES                      : 'cx.list_templates',
    GET_DATA_SCENARIOS                  : 'cx.get_data_scenarios',
    GET_TEMPLATE                        : 'cx.get_template',
    INSPECTOR                           : 'cx.inspector',
    GET_EDITED_CONFIGS_FOR_SECTION      : 'cx.get_edited_configs_for_section',
    RENDER                              : 'cx.render',
    SAVE                                : 'cx.save',
    SAVE_STRINGS                        : 'cx.save_strings',
    OVERRIDE                            : 'cx.override',
    SIGNOFF                             : 'cx.signoff',
    GET_DATA_SCENARIO_WITH_PAYLOAD      : 'cx.get_data_scenario_with_payload',
    CREATE_DATA_SCENARIO_WITH_PAYLOADS  : 'cx.create_data_scenario_with_payloads',
    CLONE_DATA_SCENARIO                 : 'cx.clone_data_scenario',
    GET_CLONE_STATUS                    : 'cx.get_clone_status',
    GET_PILOT_SCENARIO                  : 'cx.get_pilot_scenario',
    DELETE_PILOT_SCENARIO               : 'cx.delete_pilot_scenario',
    GET_WIDGETS_SDK_ENCRYPTION_KEY_PAIR : 'cx.get_widgets_sdk_encryption_key_pair',
    GET_WIDGETS_SDK_INIT_PAYLOAD        : 'cx.get_widgets_sdk_init_payload',
    HER_PREVIEW_HTML                    : 'cx.her_preview_html',
    PROXY_IMAGE                         : 'cx.proxy_image',
    GET_OPERATION_AUDITS                : 'cx.get_operation_audits',
    COMPLETE_OPERATION_AUDIT            : 'cx.complete_operation_audit',
  },

  CONFIG_OPS : {
    GET_CONFIG_REGISTRY              : 'config_ops_v1.config_registry_get',
    GET_STRING_REGISTRY              : 'config_ops_v1.string_registry_get',
    CONFIGS_CLUSTERS_LIST            : 'config_ops_v1.configs_clusters_list',
    GET_CONFIG_OVERRIDES             : 'config_ops_v1.get_config_overrides',
    GET_ALL_CONFIGS_UNRESOLVED       : 'config_ops_v1.get_all_configs_unresolved_for_project',
    PROJECT_CONFIGS_UNRESOLVED_GET   : 'config_ops_v1.project_configs_unresolved_get',
    PROJECT_CONFIGS_CLUSTERS_LIST    : 'config_ops_v1.project_configs_clusters_list',
    SAVE_PROJECT_CONFIG              : 'config_ops_v1.project_configs_upsert',
    GET_PROJECT_CONFIGS              : 'config_ops_v1.project_configs_get',
    DELETE_PROJECT_CONFIG            : 'config_ops_v1.project_config_delete',
    GET_CLUSTER_CONFIG               : 'config_ops_v1.cluster_config_get',
    GET_PILOT_CONFIGS                : 'config_ops_v1.pilot_configs_query',
    GET_PROJECT_APPLICATION_METADATA : 'config_ops_v1.project_application_metadata_get',
    GET_PROJECT_ATTRIBUTES           : 'config_ops_v1.project_attributes_get',
    RESET_PILOT_CACHE                : 'config_ops_v1.reset_pilot_cache',
    GET_PILOT_CLUSTERS               : 'config_ops_v1.pilot_clusters_get',
    PUSH_CONFIGS_TO_DATASERVER       : 'config_ops_v1.project_configs_push_to_dataserver',
    GET_PENDING_UAT_CONFIGS          : 'config_ops_v1.project_configs_pending_for_uat_push_along_with_original_value',
  },

  CONFIG_TOOLS : {
    COMPARE                  : 'config_tools_v1.compare_configs',
    COMPARE_STREAM            : 'config_tools_v1.compare_configs_stream',
    GET_COMPARE_RESULT        : 'config_tools_v1.get_compare_result',
    SYNC_PUSH                 : 'config_tools_v1.push_configs_strings_to_target',
    GET_PUSH_STATUS           : 'config_tools_v1.get_push_status',
    GET_PROMOTION_MATRIX      : 'config_tools_v1.get_promotion_matrix',
    UPSERT_PROMOTION_MATRIX   : 'config_tools_v1.upsert_promotion_matrix',
    CREATE_SHELL_BUNDLE       : 'config_tools_v1.create_shell_bundle',
    DOWNLOAD_SHELL_BUNDLE     : 'config_tools_v1.download_shell_bundle',
    GET_HISTORY               : 'config_tools_v1.get_promotion_history',
    GET_HISTORY_EVENT_DETAIL  : 'config_tools_v1.get_promotion_event_detail',
  },

  CMS_MEDIA : {
    GET_IMAGES                  : 'cms_media.get_images',
    GET_UNRESOLVED_CONFIG_VALUE : 'cms_media.get_unresolved_config_value',
    PROMOTE_CONFIG              : 'cms_media.promote_config_to_env',
    UPLOAD                      : 'cms_media.upload_media',
    REVERT_CONFIG               : 'cms_media.revert_config_to_default',
    BRAND_GUIDELINES            : 'cms_media.brand_guidelines',
    BRAND_GUIDELINE_BY_ID       : 'cms_media.brand_guideline_by_id',
    ENV_MATRIX_COMPARE          : 'cms_media.env_matrix_compare',
  },

  COLOR_PALETTE : {
    GET_IMPACT : 'color_palette.get_color_palette_impact',
    GET        : 'color_palette.get_color_palette',
    SAVE       : 'color_palette.save_color_palette',
  },

  DCX_CLUSTER_API : {
    GET_FEATURE_METRIC        : 'dcx_cluster_api.get_dcx_cluster_api_endpoint',
    GET_FEATURE_METRIC_SCHEMA : 'dcx_cluster_api.get_feature_metric_schema',
    UPDATE_DCX_CLUSTER        : 'dcx_cluster_api.update_dcx_cluster',
    FEATURE_RESOLUTION        : 'dcx_cluster_api.feature_resolution',
    PUSH_CONFIGS_STRINGS      : 'dcx_cluster_api.push_configs_strings',
    DOWNLOAD_FEATURE_METRIC   : 'dcx_cluster_api.download_feature_metric',
    GET_BULK_MANAGE           : 'dcx_cluster_api.get_bulk_manage_for_web',
  },

  CLUSTER_ATTRIBUTES : {
    CREATE_ATTRIBUTES   : 'cluster_attributes.create_attributes',
    GET_ATTRIBUTES      : 'cluster_attributes.get_attributes',
    CREATE_CLUSTER      : 'cluster_attributes.create_cluster',
    UPDATE_CLUSTER      : 'cluster_attributes.update_cluster',
    GET_CLUSTER_DETAILS : 'cluster_attributes.get_cluster_details',
    GET_CLUSTERS        : 'cluster_attributes.get_clusters',
  },

  SELF_SERVE_EMAIL : {
    GET_INGESTED_USERS   : 'self_serve_email.get_ingested_users',
    GET_USER_EMAILS      : 'self_serve_email.get_user_emails',
    GET_STATUS           : 'self_serve_email.get_self_serve_email_status',
    GET_SCHEDULER_STATUS : 'self_serve_email.get_self_serve_email_scheduler_status',
    STOP_SCHEDULER       : 'self_serve_email.stop_self_serve_email_scheduler',
    RESET_SESSION        : 'self_serve_email.reset_self_serve_email_session',
    CREATE_RUN           : 'self_serve_email.create_self_serve_email_run',
  },

  USER_METADATA : {
    CREATE                  : 'user_metadata.create_user_metadata',
    GET                     : 'user_metadata.get_user_metadata',
    ASSIGN_SHC_CLUSTER      : 'user_metadata.assign_shc_cluster',
    DELETE                  : 'user_metadata.delete_user_metadata',
    USER_WEB_PREVIEW        : 'user_metadata.user_web_preview',
    GET_SCHEDULER_HISTORY   : 'user_metadata.get_data_scheduler_history',
    GET_CONSUMPTION_DATA    : 'user_metadata.get_consumption_data',
    ASSIGN_PHYSICAL_CLUSTER : 'user_metadata.assign_physical_cluster',
    FETCH_USER_ATTRIBUTE    : 'user_metadata.fetch_user_attribute',
    UPDATE_SECONDARY_EMAIL  : 'user_metadata.update_secondary_email',
    UPDATE_NAME_EMAIL       : 'user_metadata.update_name_email',
    UPDATE_ADDRESS_ZIPCODE  : 'user_metadata.update_address_zipcode',
    GET_VALID_METER_FUEL    : 'user_metadata.get_valid_meter_fuel',
  },

  DATA_GENERATOR : {
    USER_DETAILS_CREATE    : 'data_generator_v1.data_generator_user_details_create',
    USER_DETAILS_GET       : 'data_generator_v1.data_generator_user_details_get',
    RAW_DATA_GET           : 'data_generator_v1.data_generator_raw_data_get',
    BILLING_DATA_GET       : 'data_generator_v1.data_generator_billing_data_get',
    BC_SCHEDULE_CREATE_GET : 'data_generator_v1.data_generator_bc_schedule_create_get',
    USER_UUID_GET          : 'data_generator_v1.data_generator_user_uuid_get',
    BB_USER_PROGRAM_GET    : 'data_generator_v1.data_generator_bb_user_program_get',
    SYNC_WEATHER_DATA      : 'data_generator_v1.data_generator_sync_weather_data',
  },

  RATE_TRANSITION : {
    FETCH_RATE_HISTORY     : 'rate_transition.fetch_rate_history',
    CREATE_RATE_TRANSITION : 'rate_transition.create_rate_transition',
  },

  ACCESS_TOKEN : {
    FETCH_ACCESS_TOKEN     : 'access_token.api_fetch_access_token',
    GET_RATE_CONFIGURATION : 'access_token.get_rate_configuration',
  },

  PROJECTS : {
    CREATE              : 'projects_v1.projects_create',
    UPDATE              : 'projects_v1.projects_update',
    UPDATE_UUIDS        : 'projects_v1.update_uuids_for_project',
    LIST                : 'projects_v1.projects_list',
    UNARCHIVED_LIST     : 'projects_v1.unarchived_projects_list',
    PINGPONG_CREATE     : 'projects_v1.projects_pingpong_create',
    ARCHIVE             : 'projects_v1.archive_project',
    UNARCHIVE           : 'projects_v1.unarchive_project',
    GET_LOCALES         : 'projects_v1.get_project_locales',
    STRAPI_CREATE       : 'projects_v1.project_strapi_create',
    GET_CMS_PILOTS      : 'projects_v1.get_cms_pilots',
    GET_STRAPI_PROJECTS : 'projects_v1.get_strapi_projects',
    MIGRATE             : 'projects_v1.projects_migrate',
  },

  ENVIRONMENTS : {
    CREATE           : 'environments.environments_create',
    GET              : 'environments.environments_get',
    STATUS_GET       : 'environments.environments_status_get',
    AWS_REGIONS_LIST : 'environments.environments_aws_regions_list',
    LIST_BY_UTILITY  : 'environments.environments_list_by_utility',
    PROCESS_DATA_GET : 'environments.environment_process_data_get',
    UPDATE           : 'environments.environment_update',
    SCHEDULER_START  : 'environments.environments_scheduler_start',
    SCHEDULER_STOP   : 'environments.environments_scheduler_stop',
    SCHEDULER_STATUS : 'environments.environments_scheduler_status',
    LIST_ALL         : 'environments.environments_list_all',
    SCHEDULER_CHECK  : 'environments.environments_scheduler_check',
    PIPELINE_STATUS  : 'environments.get_pipeline_status',
    PIPELINE_RETRY   : 'environments.retry_pipeline_step',
  },

  ASSOCIATED_ENVIRONMENTS : {
    GET                      : 'associated_environments.get_associated_environments',
    ADD                      : 'associated_environments.add_associated_environment',
    UPDATE                   : 'associated_environments.update_associated_environment',
    REMOVE                   : 'associated_environments.remove_associated_environment',
    SET_PRIMARY              : 'associated_environments.set_primary_environment',
    GET_PRIMARY              : 'associated_environments.get_primary_environment',
    GET_ENVIRONMENT_PROJECTS : 'associated_environments.get_environment_projects',
  },

  UTILITY_BFF : {
    LIST   : 'utility_v1.utilities_list',
    CREATE : 'utility_v1.utilities_create',
    GET    : 'utility_v1.utilities_get',
    UPDATE : 'utility_v1.utilities_update',
    DELETE : 'utility_v1.utilities_delete',
  },

  PROJECT_MANAGEMENT : {
    CREATE_PROJECT_AND_PUSH_CONFIGS : 'project_management.create_project_and_push_configs',
    VALIDATE_PROJECT                : 'project_management.validate_project',
  },

  PROJECT_PROCESS : {
    CHECK_PROJECT_STATUS : 'project_process.check_project_status',
  },

  PIPELINE : {
    GET_MASTER_DATA     : 'pipeline_v1.get_pipeline_master_data',
    GET_PIPELINE_DATA   : 'pipeline_v1.get_pipeline_data',
    PROCESS_OPERATIONS  : 'pipeline_v1.process_pipeline_operations',
    SAVE_PIPELINE_SCOPE : 'pipeline_v1.save_project_pipeline_scope',
  },

  APPLICATION_SCOPE : {
    GET_APPLICATION_SCOPE          : 'application_scope.get_application_scope',
    GET_ALL_FEATURES               : 'application_scope.get_all_features',
    SAVE_PROJECT_APPLICATION_SCOPE : 'application_scope.save_project_application_scope',
    GET_PROJECT_APPLICATION_SCOPE  : 'application_scope.get_project_application_scope',
    GET_MASTER_WITH_PROJECT_SCOPE  : 'application_scope.get_master_data_with_project_scope',
    GET_DA_APPLICATION_MASTER      : 'application_scope.get_da_application_master',
    GET_DA_APPLICATION_MASTER_BY_ID : 'application_scope.get_da_application_master_by_id',
    GET_MASTER_DATA                : 'application_scope.get_master_data',
    CREATE_MASTER_DATA             : 'application_scope.create_master_data',
    UPDATE_MASTER_DATA             : 'application_scope.update_master_data',
    DELETE_MASTER_DATA             : 'application_scope.delete_master_data',
    BULK_CREATE_MASTER_DATA        : 'application_scope.bulk_create_master_data',
  },

  AUDIT : {
    LIST_LOGS           : 'audit_v1.list_logs',
    GET_LOG             : 'audit_v1.get_log',
    GET_STATS           : 'audit_v1.get_stats',
    PURGE_LOGS          : 'audit_v1.purge_logs',
    CONFIG_HISTORY      : 'audit_v1.config_history',
    STRING_HISTORY      : 'audit_v1.string_history',
    GET_EVENT_TYPES     : 'audit_v1.get_event_types',
    CREATE_EVENT_TYPE   : 'audit_v1.create_event_type',
    GET_SOURCES         : 'audit_v1.get_sources',
    CREATE_SOURCE       : 'audit_v1.create_source',
    PROJECT_ACTIVITY    : 'audit_v1.project_activity',
    USER_ACTIVITY       : 'audit_v1.user_activity',
    SCOPE_HISTORY       : 'audit_v1.scope_history',
    CLUSTER_HISTORY     : 'audit_v1.cluster_history',
    SCENARIO_HISTORY    : 'audit_v1.scenario_history',
    LIST_AUDIT_QUEUE    : 'audit_v1.list_audit_queue',
    RETRY_AUDIT_QUEUE   : 'audit_v1.retry_audit_queue',
    CREATE_AUDIT_EVENTS : 'audit_v1.create_audit_events',
  },

  /** NBI Insights (epic DA-697). proxypulse fronts quill's insight routes
   *  (DA-2385) plus three GenAI routes of its own (DA-2401); quill is never
   *  called directly from here.
   *
   *  `@require_feature()` matches `request.endpoint`, i.e.
   *  `<blueprint>.<view function name>` — blueprint `insights`, view functions
   *  as declared in proxypulse `routes/v1/insights.py`. **These strings were
   *  verified against that file on 2026-09-11**; six of them were wrong when
   *  first written from the ticket description alone. Re-verify against the
   *  source, not a ticket, whenever a route is renamed. */
  INSIGHTS : {
    GET_CATALOG         : 'insights.get_catalog',
    GET_REVIEW_WORKFLOW : 'insights.get_review_workflow',
    /** ⚠️ GET (list) and POST (create) are ONE Flask view, so they share one
     *  feature row — proxypulse cannot grant read without also granting create.
     *  DA-2385 intended a read/write split; it is not achievable for create as
     *  implemented. UI-level gating still separates them via the
     *  `ui.cta.nbi_insights.create` id. */
    LIST_OR_CREATE      : 'insights.list_or_create_insights',
    UPDATE_ALL          : 'insights.update_insights',
    PUBLISH_ALL         : 'insights.publish_insights',
    UNPUBLISH_ALL       : 'insights.unpublish_insights',
    DISCARD_ALL_DRAFTS  : 'insights.discard_insights',
    TAG_RECOMMENDATIONS : 'insights.tag_recommendations',

    /* GenAI + validation (DA-2401). These do not relay quill content:
       generate/semantic call OpenAI in proxypulse, and validate-template-text
       relays quill's stateless token check (contract §2b). */
    VALIDATE_TEMPLATE_TEXT : 'insights.validate_template_text',
    GENERATE_ALTERNATIVE   : 'insights.generate_alternative',
    SEMANTIC_VALIDATE      : 'insights.semantic_validate',
  },

  /** proxypulse's own RBAC admin surface — used by the EntityPermissionPanel UI itself */
  RBAC : {
    LIST_FEATURES             : 'rbac.list_features',
    CREATE_FEATURE            : 'rbac.create_feature',
    UPDATE_FEATURE            : 'rbac.update_feature',
    DELETE_FEATURE            : 'rbac.delete_feature',
    ATTACH_ROLE_FEATURES      : 'rbac.attach_features_to_role',
    DETACH_ROLE_FEATURES      : 'rbac.detach_features_from_role',
    LIST_FEATURE_GROUPS       : 'rbac.list_feature_groups',
    GET_FEATURE_GROUP         : 'rbac.get_feature_group',
    CREATE_FEATURE_GROUP      : 'rbac.create_feature_group',
    UPDATE_FEATURE_GROUP      : 'rbac.update_feature_group',
    DELETE_FEATURE_GROUP      : 'rbac.delete_feature_group',
    FILE_FEATURES_INTO_GROUP  : 'rbac.file_features_into_group',
    GRANT_ROLE_FEATURE_GROUP  : 'rbac.grant_feature_group_to_role',
    REVOKE_ROLE_FEATURE_GROUP : 'rbac.revoke_feature_group_from_role',
    GRANT_USER_FEATURE        : 'rbac.grant_feature_to_user',
    REVOKE_USER_FEATURE       : 'rbac.revoke_feature_from_user',
    GRANT_USER_FEATURE_GROUP  : 'rbac.grant_feature_group_to_user',
    REVOKE_USER_FEATURE_GROUP : 'rbac.revoke_feature_group_from_user',
    ROLE_PERMISSIONS_TREE     : 'rbac.role_permissions_tree',
    USER_PERMISSIONS_TREE     : 'rbac.user_permissions_tree',
    GET_ROLE_MODEL_RESTRICTIONS    : 'rbac.get_role_model_restrictions',
    CREATE_ROLE_MODEL_RESTRICTION  : 'rbac.create_role_model_restriction',
    UPDATE_ROLE_MODEL_RESTRICTION  : 'rbac.update_role_model_restriction',
    DELETE_ROLE_MODEL_RESTRICTION  : 'rbac.delete_role_model_restriction',
    GET_USER_MODEL_RESTRICTIONS    : 'rbac.get_user_model_restrictions',
    CREATE_USER_MODEL_RESTRICTION  : 'rbac.create_user_model_restriction',
    UPDATE_USER_MODEL_RESTRICTION  : 'rbac.update_user_model_restriction',
    DELETE_USER_MODEL_RESTRICTION  : 'rbac.delete_user_model_restriction',
    LIST_USERS                : 'rbac.list_users',
    CREATE_USER               : 'rbac.create_user',
    ASSIGN_ROLES              : 'rbac.assign_roles',
    REMOVE_ROLE               : 'rbac.remove_role',
    LIST_ROLES                : 'rbac.list_roles',
    CREATE_ROLE               : 'rbac.create_role',
    UPDATE_ROLE               : 'rbac.update_role',
    DELETE_ROLE               : 'rbac.delete_role',
  },

} as const

export type BffFeatureAction = Leaves<typeof BFF_FEATURES>

/** One domain block of the BFF catalog — "user holds ANY action listed in it". */
export type BffFeatureDomain = (typeof BFF_FEATURES)[keyof typeof BFF_FEATURES]
